import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { KeyRound, Check, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Key Holder Management — service accommodation pricing block.
 * Three tiers (Basic / Standard / Premium), fetched from /api/key-holder/tiers.
 * CTA opens the unified enquiry dialog with a tier-specific subject.
 */
export const KeyHolderSection = () => {
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/key-holder/tiers`)
      .then((r) => setTiers(r.data.tiers || []))
      .catch((e) => logError("key holder tiers fetch failed", e));
  }, []);

  if (!tiers.length) return null;

  return (
    <section
      id="key-holder"
      className="py-20 md:py-28 bg-[#F9FAF9]"
      data-testid="key-holder-section"
    >
      <div className="container-clean">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2D5016] px-4 py-2 rounded-full text-sm font-medium mb-5">
            <KeyRound className="w-4 h-4" />
            Service Accommodation
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold text-[#1A2E12] tracking-tight mb-4">
            Key Holder Management
          </h2>
          <p className="text-lg text-[#5C6B57] leading-relaxed">
            Competitive-rate key holding for Airbnb hosts, serviced apartments and short-term let owners. Secure storage, meet-and-greet, and integrated Clean Kin turnaround cleans.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {tiers.map((t, i) => (
            <motion.div
              key={t.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <Card
                className={`h-full flex flex-col border-2 transition-all ${
                  t.highlight
                    ? "border-[#2D5016] shadow-lg scale-[1.02]"
                    : "border-[#E2E8E0]"
                }`}
                data-testid={`key-holder-tier-${t.slug}`}
              >
                <CardContent className="p-6 flex flex-col h-full">
                  {t.highlight && (
                    <span className="self-start bg-[#2D5016] text-white text-xs uppercase tracking-wider px-3 py-1 rounded-full mb-3 font-semibold">
                      Most popular
                    </span>
                  )}
                  <h3 className="text-2xl font-semibold text-[#1A2E12]">{t.name}</h3>
                  <p className="text-sm text-[#5C6B57] mt-1">{t.tagline}</p>
                  <div className="mt-5">
                    <span className="text-4xl font-bold text-[#2D5016]">
                      £{t.monthly_price}
                    </span>
                    <span className="text-sm text-[#5C6B57] ml-1">/ month</span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {t.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-[#1A2E12]"
                      >
                        <Check className="w-4 h-4 text-[#7CB342] mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <EnquiryDialog
                    source={`key-holder-${t.slug}`}
                    subject={`Key Holder Management — ${t.name} tier enquiry`}
                    defaultMessage={`Hi Clean Kin,\n\nI'd like to enquire about the ${t.name} tier of your Key Holder Management service (£${t.monthly_price}/month).\n\nProperty type / location:\nNumber of properties:\nExpected changeovers per month:\n\nThanks.`}
                  >
                    <Button
                      className={`w-full mt-6 rounded-full py-6 ${
                        t.highlight
                          ? "bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                          : "bg-white border-2 border-[#2D5016] text-[#2D5016] hover:bg-[#E8F5E9]"
                      }`}
                      data-testid={`key-holder-cta-${t.slug}`}
                    >
                      Enquire about {t.name}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </EnquiryDialog>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-[#5C6B57] mt-10 max-w-2xl mx-auto">
          Prices per property. Multi-property discounts available — email us with your portfolio size.
        </p>
      </div>
    </section>
  );
};

export default KeyHolderSection;
